import { FormEvent, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Edit, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminUser, getCurrentAdmin, logout } from "./api";
import {
  ProductCategory,
  ProductCategoryPayload,
  deleteAdminProductCategory,
  listAdminProductCategories,
  saveAdminProductCategory,
} from "@/services/products";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminSwitchCard } from "./components/AdminChoiceControl";

const emptyForm = {
  Name: "",
  Description: "",
  ParentCategoryId: "",
  IsActive: true,
  SortOrder: "100",
};

export default function AdminProductCategories() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [keyword, setKeyword] = useState("");
  const [expandedParentIds, setExpandedParentIds] = useState<Set<number>>(new Set());
  const { confirm, ConfirmDialog } = useConfirmDialog();

  const parentCategories = useMemo(() => categories.filter((category) => !category.parentCategoryId), [categories]);
  const childCountByParentId = useMemo(() => {
    return categories.reduce<Record<number, number>>((counts, category) => {
      if (category.parentCategoryId) counts[category.parentCategoryId] = (counts[category.parentCategoryId] || 0) + 1;
      return counts;
    }, {});
  }, [categories]);
  const filteredParents = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) return parentCategories;
    return parentCategories.filter((parent) => {
      const children = categories.filter((category) => category.parentCategoryId === parent.id);
      return [parent, ...children].some((category) =>
        `${category.name} ${category.description || ""}`.toLowerCase().includes(normalizedKeyword),
      );
    });
  }, [categories, keyword, parentCategories]);

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      const rows = await listAdminProductCategories();
      setCurrentUser(me.User);
      setCategories(rows);
      setExpandedParentIds(new Set(rows.filter((category) => !category.parentCategoryId).slice(0, 3).map((category) => category.id)));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "產品類別載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function openCreateForm(parentId = "") {
    setEditingCategory(null);
    setForm({ ...emptyForm, ParentCategoryId: parentId });
    setMessage("");
    setFormOpen(true);
  }

  function toggleParent(parentId: number) {
    setExpandedParentIds((current) => {
      const next = new Set(current);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  }

  function expandAll() {
    setExpandedParentIds(new Set(parentCategories.map((category) => category.id)));
  }

  function collapseAll() {
    setExpandedParentIds(new Set());
  }

  function openEditForm(category: ProductCategory) {
    setEditingCategory(category);
    setForm({
      Name: category.name,
      Description: category.description || "",
      ParentCategoryId: category.parentCategoryId ? String(category.parentCategoryId) : "",
      IsActive: category.isActive,
      SortOrder: String(category.sortOrder || 100),
    });
    setMessage("");
    setFormOpen(true);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.Name.trim()) {
      setMessage("請輸入產品類別名稱。");
      return;
    }

    const payload: ProductCategoryPayload = {
      Name: form.Name.trim(),
      Description: form.Description.trim(),
      ParentCategoryId: form.ParentCategoryId ? Number(form.ParentCategoryId) : null,
      IsActive: form.IsActive,
      SortOrder: Number(form.SortOrder || 0),
    };

    setSaving(true);
    setMessage("");
    try {
      await saveAdminProductCategory(editingCategory?.id || null, payload);
      setFormOpen(false);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "產品類別儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(category: ProductCategory) {
    const confirmed = await confirm({
      title: "刪除產品類別？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{category.name}</strong> 嗎？
          <br />
          若已有產品使用此類別，系統會阻止刪除，請改用停用。
        </>
      ),
      confirmText: "刪除類別",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminProductCategory(category.id);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "產品類別刪除失敗。");
    }
  }

  return (
    <AdminLayout sectionLabel="系統管理" title="產品類別" user={currentUser} onLogout={handleLogout}>
      <div className="mb-5 flex flex-col gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-bold text-[#59665f]">類別主檔</p>
          <p className="mt-1 text-sm text-[#7b877f]">例如：3C產品作為第一層，筆記型電腦、線材作為第二層。</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="inline-flex h-10 items-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={load} type="button">
            <RefreshCw size={18} />重新整理
          </button>
          <button className="inline-flex h-10 items-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" onClick={() => openCreateForm()} type="button">
            <Plus size={18} />新增第一層
          </button>
        </div>
      </div>

      {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

      <div className="mb-4 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:flex md:items-center md:justify-between">
        <div className="relative md:w-[360px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7b877f]" size={17} />
          <input
            className="h-11 w-full rounded-md border border-[#cfd8d2] pl-10 pr-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            placeholder="搜尋類別名稱或描述"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={expandAll} type="button">全部展開</button>
          <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={collapseAll} type="button">全部收合</button>
        </div>
      </div>

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[64vh] overflow-auto">
          <table className="w-full min-w-[920px] border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f]">
              <tr>
                <th className="px-4 py-3">類別名稱</th>
                <th className="px-4 py-3">父類別</th>
                <th className="px-4 py-3">描述</th>
                <th className="px-4 py-3">狀態</th>
                <th className="px-4 py-3">排序</th>
                <th className="px-4 py-3">更新時間</th>
                <th className="px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>載入中...</td></tr>
              ) : filteredParents.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>尚未建立產品類別。</td></tr>
              ) : filteredParents.flatMap((parentCategory) => {
                const children = categories.filter((category) => category.parentCategoryId === parentCategory.id);
                const normalizedKeyword = keyword.trim().toLowerCase();
                const visibleChildren = normalizedKeyword
                  ? children.filter((category) => `${category.name} ${category.description || ""}`.toLowerCase().includes(normalizedKeyword) || `${parentCategory.name} ${parentCategory.description || ""}`.toLowerCase().includes(normalizedKeyword))
                  : children;
                const parentExpanded = expandedParentIds.has(parentCategory.id) || Boolean(normalizedKeyword);
                const rows = [parentCategory, ...(parentExpanded ? visibleChildren : [])];
                return rows.map((category) => {
                const parent = categories.find((item) => item.id === category.parentCategoryId);
                const isParent = !category.parentCategoryId;
                const childCount = childCountByParentId[category.id] || 0;
                return (
                  <tr className="border-t border-[#edf0ee]" key={category.id}>
                    <td className="px-4 py-4">
                      <div className={category.parentCategoryId ? "pl-6" : ""}>
                        <div className="flex items-center gap-2 font-bold text-[#203d32]">
                          {isParent ? (
                            <button
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#315746] hover:bg-[#f0f5f2] disabled:opacity-40"
                              disabled={childCount === 0}
                              onClick={() => toggleParent(category.id)}
                              title={parentExpanded ? "收合子類別" : "展開子類別"}
                              type="button"
                            >
                              {parentExpanded ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                            </button>
                          ) : (
                            <span className="inline-flex h-7 w-7 items-center justify-center text-[#b7c1bb]">└</span>
                          )}
                          <span>{category.name}</span>
                          {isParent && childCount > 0 && <span className="rounded-full bg-[#e9f4ec] px-2 py-0.5 text-xs font-bold text-[#315746]">{childCount}</span>}
                        </div>
                        <div className="mt-1 text-xs font-bold text-[#7b877f]">{category.parentCategoryId ? "第二層類別" : "第一層類別"}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-[#59665f]">{parent?.name || "-"}</td>
                    <td className="max-w-[320px] px-4 py-4 text-sm text-[#59665f]">{category.description || "-"}</td>
                    <td className="px-4 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${category.isActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{category.isActive ? "啟用" : "停用"}</span>
                    </td>
                    <td className="px-4 py-4">{category.sortOrder}</td>
                    <td className="px-4 py-4 text-sm text-[#59665f]">{category.updatedAt || "-"}</td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        {!category.parentCategoryId && (
                          <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openCreateForm(String(category.id))} type="button">
                            <Plus size={16} />子類別
                          </button>
                        )}
                        <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openEditForm(category)} type="button">
                          <Edit size={16} />編輯
                        </button>
                        <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#f0c7c4] bg-white px-3 text-sm font-bold text-[#9a2d2d] hover:bg-[#fdebea]" onClick={() => handleDelete(category)} type="button">
                          <Trash2 size={16} />刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#10251c]/35 sm:items-center sm:px-4 sm:py-6">
          <form className="w-full max-w-[560px] rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.18)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex items-center justify-between border-b border-[#edf0ee] px-5 py-4">
              <h2 className="text-xl font-bold text-[#203d32]">{editingCategory ? "編輯產品類別" : "新增產品類別"}</h2>
              <button aria-label="關閉" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setFormOpen(false)} type="button"><X size={18} /></button>
            </div>
            <div className="grid gap-4 px-5 py-5">
              <label className="grid gap-2 text-sm font-bold text-[#315746]">類別名稱<input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={80} value={form.Name} onChange={(event) => setForm((current) => ({ ...current, Name: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                父類別
                <select className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.ParentCategoryId} onChange={(event) => setForm((current) => ({ ...current, ParentCategoryId: event.target.value }))}>
                  <option value="">無，作為第一層類別</option>
                  {parentCategories.filter((category) => category.id !== editingCategory?.id).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                </select>
              </label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">描述<textarea className="min-h-[96px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Description} onChange={(event) => setForm((current) => ({ ...current, Description: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">排序<input className="h-11 rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} /></label>
              <AdminSwitchCard
                checked={form.IsActive}
                description={form.IsActive ? "此類別可在產品表單與前台分類中使用。" : "停用後不提供新產品選用。"}
                label="啟用類別"
                onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))}
              />
            </div>
            <div className="flex flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setFormOpen(false)} type="button">取消</button>
              <button className="h-10 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={saving} type="submit">{saving ? "儲存中" : "儲存"}</button>
            </div>
          </form>
        </div>
      )}
      <ConfirmDialog />
    </AdminLayout>
  );
}
